// WS-8 P2 v2 — Facturación · Cruce / Conciliación (espejo del "Seguimiento
// semanal" del cliente).
//
// Columnas como el Excel: Semana · Día · Fecha prog. · Orden · SKU ·
// Total programado · Prod. Acumulada (EN VIVO, con ✓ validada) ·
// Cumplimiento (una columna: % + barra; muestra "→ real" si hay unidades
// no entregadas por el cliente) · Estado · Observaciones · Acciones.
//
// Estados: Sin programar · En proceso · Terminada (auto al cumplir, o cierre
// manual con motivo). Cerrar permite seleccionar los PAROS que el líder
// registró en esa OP (causa interna) y/o unidades no entregadas por el
// cliente (alimentan el REAL). Reabrir deshace un cierre por error.
// Click en la fila (o "Detalle") → modal con fechas inicio/fin, turnos,
// capturas por día y observaciones completas.

import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  semanaOperativaKeyDe,
  type CruceDetalleQuery,
  type CruceEstado,
  type CruceFila,
  type ParoConciliacion,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { KpiCard } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import {
  useCerrarCruce,
  useCruceDetalle,
  useFacturacionCruce,
  useReabrirCruce,
} from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { REGLA_SEMAFORO_COMERCIAL, semaforoComercial } from '../../lib/semaforo';
import { exportarCruce } from './factCruceExport';
import { PeriodoSelector, usePeriodoRango } from './factPeriodo';

const ESTADO_UI: Record<CruceEstado, { label: string; tone: 'ok' | 'warn' | 'info' | 'neutral' }> =
  {
    terminada: { label: 'Terminada', tone: 'ok' },
    en_proceso: { label: 'En proceso', tone: 'info' },
    sin_programar: { label: 'Sin programar', tone: 'warn' },
  };

const DOW = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('es-CO', { maximumFractionDigits: 0 });
const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${Math.round(n)}%`;

function diaDe(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
}

/** Número de semana (operativa) de una fecha YYYY-MM-DD. Con default = ISO. */
function semanaDe(iso: string | null, diaInicioSemana = 1): string {
  if (!iso) return '—';
  return String(Number(semanaOperativaKeyDe(iso.slice(0, 10), diaInicioSemana).slice(-2)));
}

const claveDe = (f: CruceFila, rango: { desde: string; hasta: string }): CruceDetalleQuery => ({
  cliente_id: f.cliente_id,
  planta_id: f.planta_id,
  numero_op: f.numero_op,
  sku_id: f.sku_id,
  desde: rango.desde,
  hasta: rango.hasta,
});

export function FacturacionCrucePage() {
  const { state, signOut } = useAuth();
  const { diaInicioSemana } = useTenantConfig();
  const { periodo, setPeriodo, rango, setRango, rangoValido, rangoEfectivo } =
    usePeriodoRango('semana');
  const [clienteId, setClienteId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<'todos' | CruceEstado>('todos');
  const [modal, setModal] = useState<
    { kind: 'cerrado' } | { kind: 'detalle'; fila: CruceFila } | { kind: 'cerrar'; fila: CruceFila }
  >({ kind: 'cerrado' });

  const { data, isLoading, isError, error, isFetching } = useFacturacionCruce({
    desde: rangoEfectivo.desde,
    hasta: rangoEfectivo.hasta,
    cliente_id: clienteId || undefined,
    area_id: areaId || undefined,
  });

  const filasVisibles =
    data === undefined
      ? []
      : estadoFiltro === 'todos'
        ? data.filas
        : data.filas.filter((f) => f.estado === estadoFiltro);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando cruce…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => signOut()} variant="outline">
          Cerrar sesión
        </Button>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Cruce / Conciliación</h1>
            <p className="text-sm text-fg-tertiary">
              Programado vs producción del líder, por OP. La acumulada avanza en vivo; la
              facturación usará solo lo validado ✓.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <PeriodoSelector
              periodo={periodo}
              onPeriodo={setPeriodo}
              rango={rango}
              onRango={setRango}
              rangoValido={rangoValido}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={filasVisibles.length === 0}
              onClick={() => exportarCruce(filasVisibles, rangoEfectivo, diaInicioSemana)}
              title="Descarga en Excel las filas visibles (respeta los filtros de cliente, área y estado)"
            >
              ↓ Exportar
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <KpiCard
            label="OPs programadas"
            value={fmt(data.kpis.ops_programadas)}
            sub={`${fmt(data.kpis.unidades_programadas)} unidades`}
          />
          <KpiCard
            label="Terminadas"
            hint={`Semáforo de compromiso comercial: ${REGLA_SEMAFORO_COMERCIAL}.`}
            value={fmt(data.kpis.terminadas)}
            sub={`cumplimiento global ${fmtPct(data.kpis.cumplimiento_global_pct)}`}
            subTone={semaforoComercial(data.kpis.cumplimiento_global_pct)}
          />
          <KpiCard
            label="En proceso"
            value={fmt(data.kpis.en_proceso)}
            sub={`${fmt(data.kpis.unidades_capturadas)} acumuladas · ${fmt(data.kpis.unidades_validadas)} ✓`}
          />
          <KpiCard
            label="Sin programar"
            value={fmt(data.kpis.sin_programar)}
            hint="OPs que los líderes capturaron pero no están cargadas en Programación por OP."
            subTone={data.kpis.sin_programar > 0 ? 'warn' : 'neutral'}
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cr-cliente">Cliente</Label>
            <Select
              id="cr-cliente"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="w-44"
            >
              <option value="">Todos</option>
              {data.clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cr-area">Área</Label>
            <Select
              id="cr-area"
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
              className="w-52"
            >
              <option value="">Todas</option>
              {data.areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.codigo} · {a.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Estado</Label>
            <SegmentedControl<'todos' | CruceEstado>
              size="sm"
              value={estadoFiltro}
              onChange={setEstadoFiltro}
              options={[
                { value: 'todos', label: 'Todos' },
                { value: 'en_proceso', label: 'En proceso' },
                { value: 'terminada', label: 'Terminadas' },
                { value: 'sin_programar', label: 'Sin programar' },
              ]}
            />
          </div>
          {isFetching ? <Spinner className="mb-2 size-4" /> : null}
        </div>

        <TablaCruce
          filas={filasVisibles}
          onDetalle={(fila) => setModal({ kind: 'detalle', fila })}
          onCerrar={(fila) => setModal({ kind: 'cerrar', fila })}
          rango={rangoEfectivo}
        />

        <footer className="text-center text-[11px] text-fg-tertiary">
          Cruce · {data.filas.length} OP(s) · {data.desde} → {data.hasta} · acumulada en vivo, ✓ =
          validada por supervisor
        </footer>
      </div>

      {modal.kind === 'detalle' ? (
        <DetalleDialog
          fila={modal.fila}
          clave={claveDe(modal.fila, rangoEfectivo)}
          onClose={() => setModal({ kind: 'cerrado' })}
        />
      ) : null}
      {modal.kind === 'cerrar' ? (
        <CerrarDialog
          fila={modal.fila}
          clave={claveDe(modal.fila, rangoEfectivo)}
          onClose={() => setModal({ kind: 'cerrado' })}
        />
      ) : null}
    </RoleLayout>
  );
}

// ============================================================================
// Tabla estilo Seguimiento semanal
// ============================================================================

function CumplimientoCelda({ fila }: { fila: CruceFila }) {
  const pct = fila.cumplimiento_pct;
  if (pct === null) return <span className="text-fg-tertiary">—</span>;
  const real = fila.cumplimiento_real_pct;
  const muestraReal = real !== null && Math.round(real) !== Math.round(pct);
  const ancho = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex min-w-24 flex-col items-end gap-1">
      <span className="tabular-nums font-medium">
        {fmtPct(pct)}
        {muestraReal ? (
          <span
            className="text-info"
            title="Cumplimiento real (ajustado por no entregadas del cliente)"
          >
            {' '}
            → {fmtPct(real)}
          </span>
        ) : null}
      </span>
      {/* DN5: semáforo COMERCIAL (95/90), no el 100/80 que tenía esta barra. */}
      <div
        className="h-1 w-full overflow-hidden rounded bg-surface-overlay"
        title={`Compromiso comercial: ${REGLA_SEMAFORO_COMERCIAL}`}
      >
        <div
          className={cn(
            'h-full rounded',
            semaforoComercial(pct) === 'ok'
              ? 'bg-ok'
              : semaforoComercial(pct) === 'warn'
                ? 'bg-warn'
                : 'bg-crit',
          )}
          style={{ width: `${ancho}%` }}
        />
      </div>
    </div>
  );
}

function TablaCruce({
  filas,
  onDetalle,
  onCerrar,
  rango,
}: {
  filas: CruceFila[];
  onDetalle: (f: CruceFila) => void;
  onCerrar: (f: CruceFila) => void;
  rango: { desde: string; hasta: string };
}) {
  const { diaInicioSemana } = useTenantConfig();
  const reabrir = useReabrirCruce();

  async function reabrirFila(f: CruceFila) {
    try {
      await reabrir.mutateAsync({
        cliente_id: f.cliente_id,
        planta_id: f.planta_id,
        numero_op: f.numero_op,
        sku_id: f.sku_id,
      });
      toast.success('Cierre reabierto');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error al reabrir';
      toast.error('No se pudo reabrir', { description: msg });
    }
  }

  if (filas.length === 0) {
    return (
      <div className="rounded border border-dashed border-border p-8 text-center text-sm text-fg-tertiary">
        No hay OPs programadas ni producción en el período.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded border border-border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-surface-overlay text-xs text-fg-tertiary">
          <tr>
            <th className="px-2 py-2 text-left">Sem.</th>
            <th className="px-2 py-2 text-left">Día</th>
            <th className="px-2 py-2 text-left">Fecha prog.</th>
            <th className="px-2 py-2 text-left">Orden</th>
            <th className="px-2 py-2 text-left">SKU</th>
            <th className="px-2 py-2 text-right">Total prog.</th>
            <th className="px-2 py-2 text-right">Prod. acum.</th>
            <th className="px-2 py-2 text-right">Cumplimiento</th>
            <th className="px-2 py-2 text-left">Estado</th>
            <th className="max-w-40 px-2 py-2 text-left">Observaciones</th>
            <th className="px-2 py-2" aria-label="Acciones" />
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const ui = ESTADO_UI[f.estado];
            const obs = f.observacion_conciliacion ?? f.observacion;
            return (
              <tr
                key={`${f.numero_op ?? 'null'}-${f.sku_id}-${f.planta_id}`}
                className="cursor-pointer border-t border-border hover:bg-surface-overlay/40"
                onClick={() => onDetalle(f)}
              >
                <td className="px-2 py-2 tabular-nums text-fg-tertiary">
                  {semanaDe(f.fecha_programada_desde, diaInicioSemana)}
                </td>
                <td className="px-2 py-2 text-xs">{diaDe(f.fecha_programada_desde)}</td>
                <td className="px-2 py-2 tabular-nums text-xs">
                  {f.fecha_programada_desde ?? '—'}
                  {f.fecha_programada_hasta && f.fecha_programada_hasta !== f.fecha_programada_desde
                    ? ` → ${f.fecha_programada_hasta}`
                    : ''}
                </td>
                <td className="px-2 py-2 tabular-nums">
                  {f.numero_op ?? <span className="text-fg-tertiary">sin OP</span>}
                </td>
                <td className="px-2 py-2">
                  <div className="font-medium">{f.sku_codigo_cliente}</div>
                  <div className="text-xs text-fg-tertiary">{f.sku_descripcion}</div>
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(f.cantidad_programada)}</td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {fmt(f.cantidad_capturada)}
                  {f.cantidad_validada < f.cantidad_capturada ? (
                    <div
                      className="text-xs text-fg-tertiary"
                      title="Validada por supervisor (base de facturación)"
                    >
                      {fmt(f.cantidad_validada)} ✓
                    </div>
                  ) : f.cantidad_capturada > 0 ? (
                    <div className="text-xs text-ok" title="Todo validado por supervisor">
                      ✓
                    </div>
                  ) : null}
                </td>
                <td className="px-2 py-2 text-right">
                  <CumplimientoCelda fila={f} />
                </td>
                <td className="px-2 py-2">
                  <Badge
                    tone={ui.tone}
                    title={
                      f.cerrada_manual
                        ? `Cerrada manualmente${f.motivo_conciliacion ? ` · ${f.motivo_conciliacion}` : ''}`
                        : undefined
                    }
                  >
                    {ui.label}
                  </Badge>
                </td>
                <td
                  className="max-w-40 truncate px-2 py-2 text-xs text-fg-tertiary"
                  title={obs ?? undefined}
                >
                  {f.motivo_conciliacion ? (
                    <span className="text-fg-secondary">{f.motivo_conciliacion} · </span>
                  ) : null}
                  {obs ?? '—'}
                </td>
                <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    {f.estado === 'en_proceso' ? (
                      <Button size="sm" variant="ghost" onClick={() => onCerrar(f)}>
                        Cerrar
                      </Button>
                    ) : null}
                    {f.cerrada_manual ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={reabrir.isPending}
                        onClick={() => void reabrirFila(f)}
                        title="Deshace el cierre (marcado por error)"
                      >
                        Reabrir
                      </Button>
                    ) : null}
                    <Button size="sm" variant="ghost" onClick={() => onDetalle(f)}>
                      Detalle
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Modal Ver detalle
// ============================================================================

function DetalleDialog({
  fila,
  clave,
  onClose,
}: {
  fila: CruceFila;
  clave: CruceDetalleQuery;
  onClose: () => void;
}) {
  const { data, isLoading } = useCruceDetalle(clave);

  return (
    <Dialog open onClose={onClose} className="max-w-2xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            OP {fila.numero_op ?? 'sin OP'} · {fila.sku_codigo_cliente} — {fila.sku_descripcion}
          </DialogTitle>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="flex justify-center p-6">
            <Spinner className="size-5" />
          </div>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: 'Inicio real', value: data.fecha_inicio ?? '—' },
                { label: 'Fin', value: data.fecha_fin ?? '—' },
                { label: 'Turnos', value: data.turnos.join(' · ') || '—' },
                {
                  label: 'Programado',
                  value: `${fmt(fila.cantidad_programada)} (${fila.fecha_programada_desde ?? '—'})`,
                },
              ].map((k) => (
                <div key={k.label} className="rounded border border-border px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                    {k.label}
                  </div>
                  <div className="mt-0.5 text-sm font-medium">{k.value}</div>
                </div>
              ))}
            </div>

            <div>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-fg-tertiary">
                Producción por día
              </h3>
              <table className="w-full text-xs">
                <thead className="text-fg-tertiary">
                  <tr>
                    <th className="py-1 text-left">Fecha</th>
                    <th className="py-1 text-left">Turno</th>
                    <th className="py-1 text-left">Línea</th>
                    <th className="py-1 text-right">Bloques</th>
                    <th className="py-1 text-right">Cantidad</th>
                    <th className="py-1 text-right">Validada ✓</th>
                  </tr>
                </thead>
                <tbody>
                  {data.capturas.map((c, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1 tabular-nums">{c.fecha}</td>
                      <td className="py-1">{c.turno_nombre}</td>
                      <td className="py-1">{c.linea_codigo}</td>
                      <td className="py-1 text-right tabular-nums">{c.bloques}</td>
                      <td className="py-1 text-right tabular-nums">{fmt(c.cantidad)}</td>
                      <td className="py-1 text-right tabular-nums">{fmt(c.cantidad_validada)}</td>
                    </tr>
                  ))}
                  {data.capturas.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-2 text-center text-fg-tertiary">
                        Sin producción capturada en el período
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {data.paros.length > 0 ? (
              <div>
                <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-fg-tertiary">
                  Paros durante la OP
                </h3>
                <ul className="flex flex-col gap-1 text-xs">
                  {data.paros.map((p) => (
                    <li key={p.registro_paro_id} className="flex justify-between gap-2">
                      <span>
                        {p.fecha} · {p.causal_madre} → {p.causal}
                        {p.observaciones ? (
                          <span className="text-fg-tertiary"> — {p.observaciones}</span>
                        ) : null}
                      </span>
                      <span className="tabular-nums text-fg-secondary">{fmt(p.minutos)} min</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {data.observaciones_programacion.length > 0 || data.conciliacion?.observacion ? (
              <div>
                <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-fg-tertiary">
                  Observaciones
                </h3>
                <ul className="flex flex-col gap-1 text-xs text-fg-secondary">
                  {data.observaciones_programacion.map((o, i) => (
                    <li key={i}>· {o}</li>
                  ))}
                  {data.conciliacion?.observacion ? (
                    <li>
                      · <span className="font-medium">Cierre:</span>{' '}
                      {data.conciliacion.motivo ? `[${data.conciliacion.motivo}] ` : ''}
                      {data.conciliacion.observacion}
                      {data.conciliacion.no_entregada_justificada > 0
                        ? ` (${fmt(data.conciliacion.no_entregada_justificada)} no entregadas por el cliente)`
                        : ''}
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar ventana
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Dialog Cerrar OP — paros del líder seleccionables + cliente
// ============================================================================

function CerrarDialog({
  fila,
  clave,
  onClose,
}: {
  fila: CruceFila;
  clave: CruceDetalleQuery;
  onClose: () => void;
}) {
  const { data: detalle, isLoading } = useCruceDetalle(clave);
  const faltante =
    fila.cantidad_programada !== null
      ? Math.max(0, fila.cantidad_programada - fila.cantidad_capturada)
      : 0;
  const [parosSel, setParosSel] = useState<Record<string, boolean>>({});
  const [noEntregada, setNoEntregada] = useState('0');
  const [observacion, setObservacion] = useState(fila.observacion_conciliacion ?? '');
  const cerrar = useCerrarCruce();

  const parosElegidos: ParoConciliacion[] = useMemo(
    () =>
      (detalle?.paros ?? [])
        .filter((p) => parosSel[p.registro_paro_id])
        .map((p) => ({
          registro_paro_id: p.registro_paro_id,
          fecha: p.fecha,
          causal_madre: p.causal_madre,
          causal: p.causal,
          minutos: p.minutos,
        })),
    [detalle, parosSel],
  );

  const noEnt = Number(noEntregada || '0');
  // Tope = faltante: justificar más de lo que faltó inflaría el REAL >100% (QA F1).
  const noEntValida = Number.isFinite(noEnt) && noEnt >= 0 && noEnt <= faltante;
  const puedeGuardar = observacion.trim().length >= 5 && noEntValida;

  async function guardar() {
    if (!puedeGuardar) return;
    try {
      await cerrar.mutateAsync({
        cliente_id: fila.cliente_id,
        planta_id: fila.planta_id,
        numero_op: fila.numero_op,
        sku_id: fila.sku_id,
        observacion_conciliacion: observacion.trim(),
        no_entregada_justificada: noEnt,
        paros: parosElegidos.length > 0 ? parosElegidos : null,
      });
      toast.success('OP cerrada');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error al cerrar';
      toast.error('No se pudo cerrar', { description: msg });
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Cerrar OP {fila.numero_op ?? 'sin OP'} · {fila.sku_codigo_cliente}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="rounded border border-border bg-surface-overlay/40 px-3 py-2 text-sm">
            <div className="flex justify-between">
              <span className="text-fg-tertiary">Programado</span>
              <span className="tabular-nums">{fmt(fila.cantidad_programada)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fg-tertiary">Prod. acumulada</span>
              <span className="tabular-nums">{fmt(fila.cantidad_capturada)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span className="text-fg-tertiary">Faltante</span>
              <span className="tabular-nums text-crit">{fmt(faltante)}</span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>¿La diferencia fue por paros? (registrados por el líder en esta OP)</Label>
            {isLoading ? (
              <Spinner className="size-4" />
            ) : (detalle?.paros.length ?? 0) === 0 ? (
              <p className="text-xs text-fg-tertiary">
                El líder no registró paros en esta OP dentro del período.
              </p>
            ) : (
              <div className="flex max-h-36 flex-col gap-1 overflow-auto rounded border border-border p-2">
                {detalle!.paros.map((p) => (
                  <label key={p.registro_paro_id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={parosSel[p.registro_paro_id] ?? false}
                      onChange={(e) =>
                        setParosSel((s) => ({ ...s, [p.registro_paro_id]: e.target.checked }))
                      }
                    />
                    <span className="flex-1">
                      {p.fecha} · {p.causal_madre} → <strong>{p.causal}</strong>
                    </span>
                    <span className="tabular-nums text-fg-tertiary">{fmt(p.minutos)} min</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cer-noent">
              Unidades NO entregadas por el cliente (0 si no aplica — ajustan el cumplimiento real)
            </Label>
            <Input
              id="cer-noent"
              type="number"
              min={0}
              max={faltante}
              value={noEntregada}
              onChange={(e) => setNoEntregada(e.target.value)}
            />
            {!noEntValida ? (
              <p className="text-xs text-crit">
                Debe estar entre 0 y el faltante ({fmt(faltante)})
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cer-obs">Observación (mín. 5 caracteres — queda auditada)</Label>
            <Textarea
              id="cer-obs"
              rows={2}
              placeholder="Ej: se cierra con faltante por paro de compresor / cliente no entregó producto"
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={!puedeGuardar || cerrar.isPending}>
            {cerrar.isPending ? 'Cerrando…' : 'Cerrar OP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
